/**
 * Dog water intake calculator — weight, activity, diet, weather.
 */
(function () {
  const ROOT_SELECTOR = '[data-dog-water-calculator]';
  const INIT_ATTR = 'data-dwc-init';
  const LB_TO_KG = 0.45359237;
  const ML_PER_OZ = 29.5735;
  const BASE_ML_PER_KG = 60;
  const RANGE_LOW_ML_PER_KG = 50;
  const RANGE_HIGH_ML_PER_KG = 70;
  const MAX_WEIGHT_KG = 200;
  const MAX_WEIGHT_LB = Math.round(MAX_WEIGHT_KG / LB_TO_KG);

  const ACTIVITY_FACTORS = {
    low: 0.9,
    moderate: 1,
    high: 1.25,
    working: 1.5,
  };

  const WEATHER_FACTORS = {
    cool: 0.9,
    mild: 1,
    warm: 1.3,
    hot: 1.5,
  };

  // Typical as-fed kcal density and moisture by diet type.
  const DIET_PROFILES = {
    dry: { kcalPerGram: 3.6, moisture: 0.1 },
    wet: { kcalPerGram: 0.95, moisture: 0.78 },
    raw: { kcalPerGram: 1.7, moisture: 0.65 },
    mixed: { kcalPerGram: 2.275, moisture: 0.44 },
  };

  // NRC-style adult MER multiplier at moderate activity; scaled by ACTIVITY_FACTORS.
  const BASE_MER_FACTOR = 1.6;

  const getStrings = (root) => {
    const strings = {};
    const template = root.querySelector('[data-dwc-strings]');

    if (!template) return strings;

    const nodes = template.content
      ? template.content.querySelectorAll('[data-key]')
      : template.querySelectorAll('[data-key]');

    for (const node of nodes) {
      const key = node.getAttribute('data-key');

      if (key) strings[key] = node.textContent || '';
    }

    return strings;
  };

  const formatTemplate = (template, replacements) => {
    let output = template || '';

    for (const [key, value] of Object.entries(replacements)) {
      output = output.split(`[[${key}]]`).join(String(value));
    }

    return output;
  };

  const roundVolume = (value) => {
    if (value >= 1000) return Math.round(value / 10) * 10;

    if (value >= 100) return Math.round(value);

    return Math.round(value * 10) / 10;
  };

  const formatNumber = (value) => {
    const rounded = roundVolume(value);

    if (Number.isInteger(rounded)) return String(rounded);

    return rounded.toFixed(1);
  };

  const formatWeight = (value) => {
    const rounded = Math.round(value * 10) / 10;

    if (Number.isInteger(rounded)) return String(rounded);

    return rounded.toFixed(1);
  };

  class DogWaterCalculator {
    constructor(root) {
      this.root = root;
      this.strings = getStrings(root);
      this.form = root.querySelector('[data-dwc-form]');
      this.weightInput = root.querySelector('[data-dwc-weight]');
      this.results = root.querySelector('[data-dwc-results]');
      this.prompt = root.querySelector('[data-dwc-prompt]');
      this.resultsBody = root.querySelector('[data-dwc-results-body]');
      this.drinkEl = root.querySelector('[data-dwc-drink]');
      this.foodEl = root.querySelector('[data-dwc-food]');
      this.totalEl = root.querySelector('[data-dwc-total]');
      this.rangeEl = root.querySelector('[data-dwc-range]');
      this.dietNote = root.querySelector('[data-dwc-diet-note]');
      this.unit = this.#checkedValue('[data-dwc-unit]', 'kg');
      this.#syncWeightConstraints();
      this.#bind();
      this.#update();
    }

    #bind() {
      if (!this.form) return;

      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
      });

      this.form.addEventListener('input', () => {
        this.#update();
      });

      this.form.addEventListener('change', (event) => {
        const target = event.target;

        if (target?.matches?.('[data-dwc-unit]')) {
          const nextUnit = target.value;

          this.#convertWeight(this.unit, nextUnit);
          this.unit = nextUnit;
          this.#syncWeightConstraints();
        }

        this.#update();
      });
    }

    #checkedValue(selector, fallback) {
      const checked = this.root.querySelector(`${selector}:checked`);

      return checked ? checked.value : fallback;
    }

    #syncWeightConstraints() {
      if (!this.weightInput) return;

      const max = this.unit === 'lb' ? MAX_WEIGHT_LB : MAX_WEIGHT_KG;

      this.weightInput.max = String(max);

      const raw = Number.parseFloat(this.weightInput.value || '');

      if (Number.isFinite(raw) && raw > max) {
        this.weightInput.value = formatWeight(max);
      }
    }

    #convertWeight(previousUnit, nextUnit) {
      if (!this.weightInput || previousUnit === nextUnit) return;

      const raw = Number.parseFloat(this.weightInput.value || '');

      if (!Number.isFinite(raw) || raw <= 0) return;

      const kg = previousUnit === 'lb' ? raw * LB_TO_KG : raw;
      const next = nextUnit === 'lb' ? kg / LB_TO_KG : kg;

      this.weightInput.value = formatWeight(next);
    }

    #weightKg() {
      const raw = Number.parseFloat(this.weightInput?.value || '');

      if (!Number.isFinite(raw) || raw <= 0) return null;

      const unit = this.#checkedValue('[data-dwc-unit]', 'kg');

      return unit === 'lb' ? raw * LB_TO_KG : raw;
    }

    #foodWaterMl(weightKg, activity, diet) {
      const profile = DIET_PROFILES[diet] ?? DIET_PROFILES.dry;
      const merFactor = BASE_MER_FACTOR * (ACTIVITY_FACTORS[activity] ?? 1);
      const rer = 70 * Math.pow(weightKg, 0.75);
      const mer = rer * merFactor;
      const foodGrams = mer / profile.kcalPerGram;

      return Math.max(0, foodGrams * profile.moisture);
    }

    #toDisplayVolume(ml, unit) {
      return unit === 'lb' ? ml / ML_PER_OZ : ml;
    }

    #formatDisplayVolume(value, unit) {
      const displayUnit = unit === 'lb' ? this.strings.oz : this.strings.ml;

      return formatTemplate(this.strings.volume, {
        value: formatNumber(value),
        unit: displayUnit,
      });
    }

    #formatRange(lowMl, highMl, unit) {
      if (unit === 'lb') {
        return formatTemplate(this.strings.normalRange, {
          low: formatNumber(lowMl / ML_PER_OZ),
          high: formatNumber(highMl / ML_PER_OZ),
          unit: this.strings.oz,
        });
      }

      return formatTemplate(this.strings.normalRange, {
        low: formatNumber(lowMl),
        high: formatNumber(highMl),
        unit: this.strings.ml,
      });
    }

    #update() {
      const weightKg = this.#weightKg();
      const unit = this.#checkedValue('[data-dwc-unit]', 'kg');
      const activity = this.#checkedValue('[data-dwc-activity]', 'moderate');
      const diet = this.#checkedValue('[data-dwc-diet]', 'dry');
      const weather = this.#checkedValue('[data-dwc-weather]', 'mild');

      if (weightKg === null) {
        this.results?.setAttribute('data-state', 'empty');
        if (this.prompt) this.prompt.hidden = false;
        if (this.resultsBody) this.resultsBody.hidden = true;
        return;
      }

      const activityFactor = ACTIVITY_FACTORS[activity] ?? 1;
      const weatherFactor = WEATHER_FACTORS[weather] ?? 1;
      const multiplier = activityFactor * weatherFactor;

      const totalMl = weightKg * BASE_ML_PER_KG * multiplier;
      const foodMl = Math.min(this.#foodWaterMl(weightKg, activity, diet), totalMl);
      const drinkMl = Math.max(0, totalMl - foodMl);
      const rangeLowMl = weightKg * RANGE_LOW_ML_PER_KG * multiplier;
      const rangeHighMl = weightKg * RANGE_HIGH_ML_PER_KG * multiplier;

      const totalRounded = roundVolume(this.#toDisplayVolume(totalMl, unit));
      const foodRounded = Math.min(roundVolume(this.#toDisplayVolume(foodMl, unit)), totalRounded);
      const drinkRounded = Math.max(0, totalRounded - foodRounded);

      if (this.drinkEl) {
        this.drinkEl.textContent = this.#formatDisplayVolume(drinkRounded, unit);
      }

      if (this.foodEl) {
        this.foodEl.textContent = this.#formatDisplayVolume(foodRounded, unit);
      }

      if (this.totalEl) {
        this.totalEl.textContent = this.#formatDisplayVolume(totalRounded, unit);
      }

      if (this.rangeEl) {
        this.rangeEl.textContent = this.#formatRange(rangeLowMl, rangeHighMl, unit);
      }

      if (this.dietNote) {
        if (diet === 'wet') {
          this.dietNote.textContent = this.strings.dietNoteWet || '';
          this.dietNote.hidden = false;
        } else if (diet === 'raw') {
          this.dietNote.textContent = this.strings.dietNoteRaw || '';
          this.dietNote.hidden = false;
        } else {
          this.dietNote.textContent = '';
          this.dietNote.hidden = true;
        }
      }

      this.results?.setAttribute('data-state', 'ready');
      if (this.prompt) this.prompt.hidden = true;
      if (this.resultsBody) this.resultsBody.hidden = false;
    }
  }

  const initRoot = (root) => {
    if (!root || root.hasAttribute(INIT_ATTR)) return;

    root.setAttribute(INIT_ATTR, '');
    new DogWaterCalculator(root);
  };

  const initAll = () => {
    const roots = document.querySelectorAll(ROOT_SELECTOR);

    for (const root of roots) {
      initRoot(root);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target?.querySelector?.(ROOT_SELECTOR) || event.target;

    if (root?.matches?.(ROOT_SELECTOR)) initRoot(root);
  });
})();
